<template>
  <section class="marginTopBodyReportCard">
    <div class="border-line-bottom">
      <select v-model.number="reportCardClass" class="report__card pl-2 without-outline">
        <option :value="0" selected disabled hidden>{{ t('option_default') }}</option>
        <option
          v-for="option in reportCardOptions"
          :key="`year-${option.year}`"
          class="report__card__option mr-2"
          :value="option.year"
        >
          {{ option.course || selectedGroupCourse }} {{ t('class') }}
        </option>
      </select>
    </div>

    <QueryErrorNotice :error="reportCardPageErrors" />

    <div class="fourth__head__parent">
      <div class="lesson__name"></div>
      <div class="d-flex flex-column fourth__head__top">
        <div class="text-center pb-3">{{ t('fourth') }}</div>
        <div class="d-flex fourth__body">
          <span v-for="item in fourth" :key="`head-fourth-${item}`" class="fourth__item">{{ item }}</span>
        </div>
      </div>
      <div v-if="showExamColumns" class="total__body">
        <div class="d-flex annual__estimate__head text-center">
          <span>{{ t('yearGrade') }}</span>
        </div>
        <div class="d-flex annual__estimate__head text-center">
          <span>{{ t('exam') }}</span>
        </div>
        <div class="d-flex exam__estimate__head text-center">
          <span>{{ t('total') }}</span>
        </div>
      </div>
      <div v-else>
        <div class="d-flex annual__estimate__head__grade text-center">
          <span>{{ t('yearGrade') }}</span>
        </div>
      </div>
    </div>

    <div class="mb-4">
      <div v-if="!isLoading && !lessons.length" class="empty__lesson mt-3">
        <div class="warning__noData text-center">
          {{ t('noData') }}
        </div>
      </div>

      <div v-else-if="!isLoading">
        <ReportCardSubjectRow
          v-for="(item, index) in lessons"
          :key="`lesson-${index}-${item.name}`"
          :show-exam-columns="showExamColumns"
          :lesson="{
            name: item.name,
            grade: [item.grade[1], item.grade[2], item.grade[3], item.grade[4]],
            yearGrade: item.yearGrade,
            exam: item.exam,
            final: item.final
          }"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import ReportCardSubjectRow from '@/components/report-card/ReportCardSubjectRow.vue';
import QueryErrorNotice from '@/components/common/QueryErrorNotice.vue';
import {
  useGradesAssignmentsQuery,
  useGroupQuery,
  usePeriodsQuery,
  useReportCardAnnualMarksQuery,
  useReportCardExamMarksQuery,
  useReportCardFinalMarksQuery,
  useReportCardParallelsQuery,
  useReportCardQuarterMarksQuery,
  useReportCardSubjectsByIdsQuery
} from '@/queries';
import { useLoadingStore } from '@/stores/loading';
import { useTopbarStore } from '@/stores/topbar';
import { useViewerStore } from '@/stores/viewer';

const { t } = useI18n();
const topbarStore = useTopbarStore();
const loadingStore = useLoadingStore();
const viewerStore = useViewerStore();
viewerStore.initFromStorage();

const reportCardClass = ref(0);
const fourth = [1, 2, 3, 4];

const selectedChild = computed(() => viewerStore.selectedChild || null);
const pupilId = computed(() => Number(selectedChild.value?.pupil_id || viewerStore.profile?.pupil_id || 0));
const groupId = computed(() => Number(selectedChild.value?.group_id || viewerStore.profile?.group_id || 0));
const groupQuery = useGroupQuery(groupId, {
  staleTime: 5 * 60 * 1000,
  retry: false
});

const periodsQuery = usePeriodsQuery({}, { staleTime: 5 * 60 * 1000 });
const assignmentsQuery = useGradesAssignmentsQuery(groupId, {
  placeholderData: (previousData) => previousData,
  retry: false
});
const parallelsQuery = useReportCardParallelsQuery(pupilId, {
  placeholderData: (previousData) => previousData,
  retry: false
});
const quarterMarksQuery = useReportCardQuarterMarksQuery(pupilId, {
  placeholderData: (previousData) => previousData,
  retry: false
});
const annualMarksQuery = useReportCardAnnualMarksQuery(pupilId, {
  placeholderData: (previousData) => previousData,
  retry: false
});
const examMarksQuery = useReportCardExamMarksQuery(pupilId, {
  placeholderData: (previousData) => previousData,
  retry: false
});
const finalMarksQuery = useReportCardFinalMarksQuery(pupilId, {
  placeholderData: (previousData) => previousData,
  retry: false
});

function toRowData(item) {
  if (item && typeof item === 'object' && item._data) return item._data;
  return item || {};
}

function parseCourseValue(value) {
  if (value === null || value === undefined) return 0;
  const direct = Number(value);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const match = String(value).match(/\d+/);
  return match ? Number(match[0]) : 0;
}

function parseYearValue(value) {
  if (value === null || value === undefined) return 0;
  const direct = Number(value);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const match = String(value).match(/\d{4}/);
  return match ? Number(match[0]) : 0;
}

function inferCourseByYear(year, currentCourse, currentAcademicYear) {
  const y = Number(year || 0);
  const c = Number(currentCourse || 0);
  const ay = Number(currentAcademicYear || 0);
  if (!y || !c || !ay) return 0;

  const diff = ay - y;
  const inferred = c - diff;
  if (inferred >= 1 && inferred <= 12) return inferred;
  return 0;
}

function parseParallelRow(row) {
  const source = row && typeof row === 'object' ? row : {};

  let year = parseYearValue(source.year || source.academic_year || source.study_year || 0);
  let course = parseCourseValue(source.parallel || source.course || source.class || source.group || source.name || 0);

  if (!year || !course) {
    const values = Object.values(source);
    for (let i = 0; i < values.length; i += 1) {
      const value = values[i];
      if (!year) year = parseYearValue(value);
      if (!course) course = parseCourseValue(value);
      if (year && course) break;
    }
  }

  return { year, course };
}

const assignments = computed(() => (Array.isArray(assignmentsQuery.data.value) ? assignmentsQuery.data.value : []));
const parallels = computed(() => (Array.isArray(parallelsQuery.data.value) ? parallelsQuery.data.value : []).map(toRowData));
const quarterMarks = computed(() => (Array.isArray(quarterMarksQuery.data.value) ? quarterMarksQuery.data.value : []).map(toRowData));
const annualMarks = computed(() => (Array.isArray(annualMarksQuery.data.value) ? annualMarksQuery.data.value : []).map(toRowData));
const examMarks = computed(() => (Array.isArray(examMarksQuery.data.value) ? examMarksQuery.data.value : []).map(toRowData));
const finalMarks = computed(() => (Array.isArray(finalMarksQuery.data.value) ? finalMarksQuery.data.value : []).map(toRowData));

const selectedGroupCourse = computed(() => {
  const fromGroupApi = parseCourseValue(groupQuery.data.value?.course || groupQuery.data.value?.parallel || 0);
  if (fromGroupApi > 0) return fromGroupApi;

  const raw = parseCourseValue(selectedChild.value?.group_course || selectedChild.value?.course || 0);
  if (raw > 0) return raw;
  const byGroupName = String(selectedChild.value?.group_name || '').match(/^(\d+)/);
  return byGroupName ? Number(byGroupName[1]) : 0;
});

const academicYear = computed(() => {
  const periods = Array.isArray(periodsQuery.data.value) ? periodsQuery.data.value : [];
  const first = periods[0];
  const row = toRowData(first);
  return Number(row?.academic_year || 0);
});

const reportCardOptions = computed(() => {
  const yearsMap = new Map();

  parallels.value.forEach((row) => {
    const parsed = parseParallelRow(row);
    const year = parsed.year;
    let course = parsed.course;
    if (!course) {
      course = inferCourseByYear(year, selectedGroupCourse.value, academicYear.value);
    }
    if (!year || !course) return;
    yearsMap.set(year, { year, course });
  });

  const quarterYears = new Set();
  let latestYear = 0;
  quarterMarks.value.forEach((row) => {
    const year = parseYearValue(row?.year || row?.academic_year || 0);
    if (year) quarterYears.add(year);
    if (year > latestYear) latestYear = year;
  });

  quarterYears.forEach((year) => {
    if (yearsMap.has(year)) return;
    const inferredCourse =
      inferCourseByYear(year, selectedGroupCourse.value, academicYear.value) ||
      selectedGroupCourse.value ||
      0;
    yearsMap.set(year, {
      year,
      course: inferredCourse
    });
  });

  const hasCurrentCourse = [...yearsMap.values()].some((item) => Number(item.course) === Number(selectedGroupCourse.value || 0));
  if (!hasCurrentCourse && academicYear.value && selectedGroupCourse.value && latestYear !== academicYear.value) {
    yearsMap.set(academicYear.value, { year: academicYear.value, course: selectedGroupCourse.value });
  }

  if (!latestYear && academicYear.value) latestYear = academicYear.value;

  const list = [...yearsMap.values()]
    .map((item) => {
      const course =
        Number(item.course || 0) ||
        inferCourseByYear(item.year, selectedGroupCourse.value, academicYear.value) ||
        0;
      return { ...item, course };
    })
    .sort((a, b) => Number(a.year) - Number(b.year));

  const anchor =
    [...list].reverse().find((item) => Number(item.course || 0) > 0) ||
    list.find((item) => Number(item.course || 0) > 0) ||
    null;
  if (anchor) {
    for (let i = 0; i < list.length; i += 1) {
      if (Number(list[i].course || 0) > 0) continue;
      const inferredFromAnchor = Number(anchor.course) - (Number(anchor.year) - Number(list[i].year));
      if (inferredFromAnchor >= 1 && inferredFromAnchor <= 12) {
        list[i].course = inferredFromAnchor;
      }
    }
  }

  if (latestYear && !list.some((item) => item.year === latestYear)) {
    const inferredCourse =
      inferCourseByYear(latestYear, selectedGroupCourse.value, academicYear.value) ||
      selectedGroupCourse.value ||
      0;
    list.push({ year: latestYear, course: inferredCourse });
  }
  return list.filter((item) => Number(item.year) > 0);
});

const latestYear = computed(() => {
  if (!reportCardOptions.value.length) return 0;
  return Math.max(...reportCardOptions.value.map((item) => Number(item.year || 0)));
});

watch(
  () => reportCardOptions.value,
  (options) => {
    if (!options.length) {
      reportCardClass.value = 0;
      return;
    }
    const current = Number(reportCardClass.value || 0);
    if (!current || !options.some((item) => Number(item.year) === current)) {
      reportCardClass.value = Number(latestYear.value || options[options.length - 1].year || 0);
    }
  },
  { immediate: true }
);

const selectedYear = computed(() => Number(reportCardClass.value || 0));
const showExamColumns = computed(() => {
  const courseByYear = reportCardOptions.value.find((item) => Number(item.year) === selectedYear.value);
  return Number(courseByYear?.course || 0) >= 5;
});

const quarterByYear = computed(() => quarterMarks.value.filter((mark) => Number(mark?.year || 0) === selectedYear.value));
const annualByYear = computed(() => annualMarks.value.filter((mark) => Number(mark?.year || 0) === selectedYear.value));
const examByYear = computed(() => examMarks.value.filter((mark) => Number(mark?.year || 0) === selectedYear.value));
const finalByYear = computed(() => finalMarks.value.filter((mark) => Number(mark?.year || 0) === selectedYear.value));

const subjectMapFromAssignments = computed(() => {
  const map = new Map();
  assignments.value.forEach((item) => {
    const subject = item?._subject || item?.subject || null;
    const id = Number(subject?.id || 0);
    if (!id) return;
    if (!map.has(id)) {
      map.set(id, subject?.name || '');
    }
  });
  return map;
});

const electiveSubjectIds = computed(() => {
  const ids = new Set();
  quarterByYear.value.forEach((item) => {
    const subjectId = Number(item?.s_id || 0);
    if (!subjectId) return;
    if (!subjectMapFromAssignments.value.has(subjectId)) {
      ids.add(subjectId);
    }
  });
  return [...ids];
});

const electiveSubjectsQuery = useReportCardSubjectsByIdsQuery(electiveSubjectIds, {
  placeholderData: (previousData) => previousData,
  retry: false
});

const subjectMap = computed(() => {
  const map = new Map(subjectMapFromAssignments.value);
  const extraSubjects = Array.isArray(electiveSubjectsQuery.data.value) ? electiveSubjectsQuery.data.value : [];
  extraSubjects.forEach((item) => {
    const row = toRowData(item);
    const id = Number(row?.id || 0);
    if (!id) return;
    if (!map.has(id)) {
      map.set(id, row?.name || '');
    }
  });
  return map;
});

function markToText(value) {
  if (value === null || value === undefined || value === '') return '';
  return String(value);
}

const lessons = computed(() => {
  const map = new Map();
  const ensureLesson = (subjectId) => {
    const id = Number(subjectId || 0);
    if (!id) return null;
    if (!map.has(id)) {
      map.set(id, {
        name: subjectMap.value.get(id) || '',
        grade: {},
        yearGrade: '',
        exam: '',
        final: ''
      });
    }
    const lesson = map.get(id);
    if (!lesson.name) lesson.name = subjectMap.value.get(id) || '';
    return lesson;
  };

  quarterByYear.value.forEach((item) => {
    const lesson = ensureLesson(item?.s_id);
    if (!lesson) return;
    const qNum = Number(item?.q_num || 0);
    if (qNum >= 1 && qNum <= 4) {
      lesson.grade[qNum] = markToText(item?.mark_value);
    }
  });

  annualByYear.value.forEach((item) => {
    const lesson = ensureLesson(item?.s_id);
    if (!lesson) return;
    lesson.yearGrade = markToText(item?.mark_value);
  });

  examByYear.value.forEach((item) => {
    const lesson = ensureLesson(item?.s_id);
    if (!lesson) return;
    lesson.exam = markToText(item?.mark_value);
  });

  finalByYear.value.forEach((item) => {
    const lesson = ensureLesson(item?.s_id);
    if (!lesson) return;
    lesson.final = markToText(item?.mark_value);
  });

  return [...map.values()]
    .filter((item) => {
      const hasQuarter = Object.keys(item.grade).length > 0;
      return hasQuarter || item.yearGrade !== '';
    })
    .sort((a, b) =>
      String(a.name || '')
        .trim()
        .localeCompare(String(b.name || '').trim(), ['ru', 'kk'], {
          sensitivity: 'base',
          ignorePunctuation: true,
          numeric: true
        })
    );
});

const isLoading = computed(
  () =>
    periodsQuery.isFetching.value ||
    groupQuery.isFetching.value ||
    assignmentsQuery.isFetching.value ||
    parallelsQuery.isFetching.value ||
    quarterMarksQuery.isFetching.value ||
    annualMarksQuery.isFetching.value ||
    examMarksQuery.isFetching.value ||
    finalMarksQuery.isFetching.value ||
    electiveSubjectsQuery.isFetching.value
);
const reportCardPageErrors = computed(() =>
  [
    periodsQuery,
    groupQuery,
    assignmentsQuery,
    parallelsQuery,
    quarterMarksQuery,
    annualMarksQuery,
    examMarksQuery,
    finalMarksQuery,
    electiveSubjectsQuery
  ]
    .filter((query) => query.isError?.value)
    .map((query) => query.error?.value)
);

watch(
  () => isLoading.value,
  (value) => {
    loadingStore.set('report-card-page', value);
  },
  { immediate: true }
);

onMounted(() => {
  topbarStore.setTitle(t('child-report-card'));
  topbarStore.addButton({
    type: 'right',
    icon: 'loop',
    action: () => {
      periodsQuery.refetch();
      groupQuery.refetch();
      assignmentsQuery.refetch();
      parallelsQuery.refetch();
      quarterMarksQuery.refetch();
      annualMarksQuery.refetch();
      examMarksQuery.refetch();
      finalMarksQuery.refetch();
      electiveSubjectsQuery.refetch();
    }
  });
});

onBeforeUnmount(() => {
  loadingStore.hide('report-card-page');
});
</script>

<style scoped lang="scss">
.lesson__name {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  width: 35% !important;
  display: flex;
  align-items: center;
}

.marginTopBodyReportCard {
  @media screen and (max-width: 768px) {
    padding: 8px;
  }

  @media screen and (min-width: 768px) {
    padding: 0 45px 5px;
  }
}

.fourth__head__parent {
  display: flex;
  justify-content: flex-start;
  border-bottom: 1px dashed #bdbdbd;
  font-family: Roboto, sans-serif;

  @media screen and (min-width: 768px) {
    font-style: normal;
    font-weight: 500;
    font-size: 20px;
    color: #164922 !important;
  }
}

.warning__noData {
  color: #fbad2f !important;
  border: 1px solid #fbd99f !important;
  background: #fff8e8;
  margin: 0 20px !important;
  font-size: 14px;
  border-radius: 4px;
  padding: 14px 12px;
}

.empty__lesson {
  display: flex;
  justify-content: center;
}

.exam__estimate__head {
  align-items: flex-start;
  justify-content: center;
  width: 100% !important;

  @media screen and (max-width: 768px) {
    font-size: 15px;
    writing-mode: vertical-rl;
    align-items: center;
    justify-content: center;
  }

  @media screen and (min-width: 768px) {
    text-align: center;
    padding-top: 16px;
  }
}

.total__body {
  width: 40%;
  display: flex;
}

.annual__estimate__head {
  align-items: flex-start;
  justify-content: center;
  border-right: 1px dashed #bdbdbd;
  width: 100% !important;

  @media screen and (max-width: 768px) {
    font-size: 15px;
    writing-mode: vertical-rl;
    align-items: center;
    justify-content: center;
  }

  @media screen and (min-width: 768px) {
    text-align: center;
    overflow: hidden;
    padding-top: 16px;
  }
}

.annual__estimate__head__grade {
  padding: 0 5px;
  align-items: flex-start;

  @media screen and (max-width: 768px) {
    width: 61px;
    font-size: 15px;
  }

  @media screen and (min-width: 768px) {
    width: 100px;
    text-align: center;
    padding-top: 16px;
    padding-left: 20px;
  }
}

.fourth__head__top {
  border-left: 1px dashed #bdbdbd;
  border-right: 1px dashed #bdbdbd;
  width: 44%;

  @media screen and (min-width: 768px) {
    padding-top: 16px;
  }
}

.fourth__body {
  display: flex;
  justify-content: space-between;
}

.report__card__option {
  padding: 5px;
}

.report__card {
  width: 100%;
  border: none !important;
  background: none !important;

  @media screen and (max-width: 768px) {
    height: 56px;
  }

  @media screen and (min-width: 768px) {
    height: 58px;
    margin-top: 0;
    font-size: 20px;
  }
}

.report__card:focus,
.report__card:active,
.report__card:focus-visible {
  outline: none !important;
  border: none !important;
  box-shadow: none !important;
}

@media screen and (max-width: 768px) {
  .lesson__name {
    width: 33% !important;
    min-width: 0;
  }

  .fourth__head__top {
    width: 34%;
    min-width: 0;
  }

  .total__body {
    width: 33%;
    min-width: 0;
  }

  .annual__estimate__head,
  .exam__estimate__head,
  .annual__estimate__head__grade {
    flex: 1 1 0;
    min-width: 0;
    font-size: 12px;
    line-height: 1.1;
  }
}

.fourth__item {
  height: 30px;
  width: 100%;
  display: flex;
  justify-content: center;
}
</style>
